import React, { useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import Modal from 'react-responsive-modal'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { addRoomType, editRoomType } from '../../actions/roomTypes'
import { Input, Checkbox } from '../../components/ModalInputs'
import Button from '../../components/Button'
import Confirmation from '../../components/Confirmation/Confirmation'
import styles from './styles.module.scss'

const INITIAL_VALUES = {
  number: '',
  name: '',
  scheduling: false
}

const validationSchema = Yup.object().shape({
  number: Yup.string()
    .max(50, 'Должно быть не более 50 символов'),
  name: Yup.string()
    .required('Заполните название')
    .max(500, 'Должно быть не более 500 символов'),
})

const RoomTypeModal = ({ open, onClose, data, addMode }) => {
  const dispatch = useDispatch()
  const [confirmation, setConfirmation] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const createItem = useCallback(async (values) => {
    setIsSubmitting(true)
    try {
      const success = await dispatch(addRoomType(values))
      if (success) onClose()
    } finally {
      setIsSubmitting(false)
    }
  }, [dispatch, onClose])

  const updateItem = useCallback(async (values) => {
    setIsSubmitting(true)
    try {
      const success = await dispatch(editRoomType(values))
      if (success) onClose()
    } finally {
      setIsSubmitting(false)
    }
  }, [dispatch, onClose])

  const {
    values,
    errors,
    touched,
    dirty,
    handleBlur,
    handleSubmit,
    handleChange,
    setFieldValue,
  } = useFormik({
    // enableReinitialize сбрасывает форму при смене data (открытие/закрытие модалки)
    initialValues: data?.rtid ? data : INITIAL_VALUES,
    enableReinitialize: true,
    validationSchema,
    onSubmit: addMode ? createItem : updateItem
  })

  const handleCloseModal = useCallback(() => {
    if (dirty) {
      setConfirmation(true)
    } else {
      onClose()
    }
  }, [dirty, onClose])

  const acceptConfirmation = useCallback(() => {
    onClose()
    setConfirmation(false)
  }, [onClose])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(false)
  }, [])

  const handleCheckboxChange = useCallback(() => {
    setFieldValue('scheduling', !values.scheduling)
  }, [setFieldValue, values.scheduling])

  const getError = useCallback((fieldName) =>
    touched[fieldName] && errors[fieldName],
    [touched, errors]
  )

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      classNames={{ modal: styles.modal }}
      closeOnEsc={!dirty}
      closeOnOverlayClick={!dirty}
      showCloseIcon={false}
      animationDuration={200}
      center
    >
      <form onSubmit={handleSubmit} className={styles.modalForm}>
        <div className={styles.modalMain}>
          <h2>{addMode ? 'Добавление типа кабинета' : 'Редактирование типа кабинета'}</h2>

          <Input
            name="number"
            label="Номер"
            value={values.number || ''}
            error={getError('number')}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting}
          />

          <Input
            name="name"
            label="Название"
            required
            value={values.name || ''}
            error={getError('name')}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting}
            autoFocus
          />

          <Checkbox
            name="scheduling"
            label="Участвует в расписании"
            checked={!!values.scheduling}
            onChange={handleCheckboxChange}
            disabled={isSubmitting}
          />
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.footerInfo} />
          <div className={styles.footerButtons}>
            <Button
              mode="white"
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting || !dirty}
            >
              Сохранить
            </Button>
            <Button
              mode="white"
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              Закрыть
            </Button>
          </div>
        </div>
      </form>

      {confirmation && (
        <Confirmation
          title="Есть несохраненные данные. Уверены что хотите выйти?"
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
        />
      )}
    </Modal>
  )
}

export default RoomTypeModal
